import React, { useState, useEffect } from 'react';
import { useLang } from './lang';
import { authHeaders } from './apiAuth';
import MesMots from './MesMots';

// Modale de compte OPTIONNEL. Ouverte depuis l'entrée discrète du footer et depuis
// le bilan de série. Trois états : créer / se connecter / connecté. La connexion sur
// un autre appareil propose de FUSIONNER la progression anonyme locale (cas
// cross-domaine).
// Le copier/coller d'UUID « avancé » a été retiré : le compte est désormais LE seul
// chemin pour transporter sa progression, et deux mécanismes concurrents pour le
// même besoin ne faisaient qu'alourdir le panneau.
const AccountPanel = ({ auth, userId, stats, apiLang, onStartSerie, onClose }) => {
  const lang = useLang();
  const t = lang.ui.account;
  const c = lang.ui.session; // libellés des compteurs (jours / mots vus)

  const [mode, setMode] = useState('create');      // 'create' | 'login'
  const [handle, setHandle] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingMerge, setPendingMerge] = useState(null); // { token, uid }
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const errText = (code) => (t.errors[code] || t.errors.generic);

  const submitCreate = async () => {
    if (busy) return;
    setBusy(true); setError('');
    const r = await auth.register(handle.trim(), passphrase, userId);
    if (!r.ok) { setError(errText(r.error)); setBusy(false); return; }
    // Événement Umami : Umami ne voit PAS les inscriptions (elles passent par
    // l'API, pas par une page vue) ; ce ping les rend visibles dans le tableau
    // de bord — anonymement, sans le pseudo. Envoyé AVANT `adopt` (qui recharge) :
    // le transport d'Umami (sendBeacon) survit au déchargement de la page.
    try { window.umami?.track?.('account_created'); } catch {}
    auth.adopt(r.token, r.uid); // stocke jeton + UUID puis recharge
  };

  const submitLogin = async () => {
    if (busy) return;
    setBusy(true); setError('');
    const r = await auth.login(handle.trim(), passphrase);
    if (!r.ok) { setError(errText(r.error)); setBusy(false); return; }
    // UUID du compte différent de l'UUID local ET progression locale non vide
    // → proposer la fusion avant de basculer (cas cross-domaine).
    // On mesure « non vide » avec les mots VUS, pas les mots maîtrisés : `mastered`
    // compte les mots à 5 rappels, ce que le SRS étale sur plusieurs jours, donc il
    // vaut 0 pour la quasi-totalité des gens. La fusion n'était jamais proposée, et
    // la progression anonyme partait en silence à la connexion.
    if (userId && r.uid && r.uid !== userId) {
      let seen = 0;
      try { const s = await fetch(`/api/stats/${userId}`, { headers: authHeaders() }).then((x) => x.json()); seen = s.seen || 0; } catch {}
      if (seen > 0) { setPendingMerge({ token: r.token, uid: r.uid }); setBusy(false); return; }
    }
    auth.adopt(r.token, r.uid);
  };

  const resolveMerge = async (yes) => {
    if (busy) return;
    setBusy(true);
    const { token, uid } = pendingMerge;
    if (yes) await auth.mergeInto(token, userId);
    auth.adopt(token, uid);
  };

  const onSubmit = (e) => { e.preventDefault(); (mode === 'create' ? submitCreate : submitLogin)(); };

  const field = "w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:border-blue-400 focus:outline-none";
  const primaryBtn = "w-full bg-blue-500 hover:bg-blue-400 text-white rounded py-2 text-sm font-semibold transition-colors disabled:opacity-50";

  const stop = (e) => e.stopPropagation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="ascenseur-discret w-full max-w-sm max-h-[88vh] overflow-y-auto rounded-xl bg-slate-800 border border-slate-700 p-5 text-left text-slate-200 shadow-xl" onClick={stop}>

        <div className="flex items-start justify-between mb-3">
          <h2 className="text-white font-semibold text-base">{t.title}</h2>
          <button onClick={onClose} aria-label={t.close} className="text-slate-500 hover:text-slate-200 text-xl leading-none -mt-1 px-1">×</button>
        </div>

        {/* Les TOTAUX cumulés vivent ici, pas sur le bilan : le bilan ne montre que
            ce qu'on vient de gagner, et la surface du quiz ne montre rien. Ce
            panneau s'appelle « Votre progression », c'est sa place. */}
        {stats && (stats.couverture > 0 || stats.days > 0) && !pendingMerge && (
          <div className="flex items-center gap-4 mb-4 pb-3 border-b border-slate-700/60 text-xs text-slate-500">
            <span className="tabular-nums">{lang.ui.couverture(stats.couverture)}</span>
            <span>
              <span className="font-bold text-slate-300 tabular-nums">{stats.days}</span> {c.days(stats.days)}
            </span>
            {stats.seen > 0 && (
              <span>
                <span className="font-bold text-slate-300 tabular-nums">{stats.seen}</span> {c.seen}
              </span>
            )}
          </div>
        )}

        {/* Les trois listes de mots. Elles ne dépendent PAS d'un compte : la
            progression anonyme est une vraie progression, et c'est justement
            l'écran où on lui donne de la valeur. Masquées pendant une invite de
            fusion, qui demande une réponse et ne doit pas être noyée. */}
        {!pendingMerge && (
          <MesMots
            userId={userId}
            apiLang={apiLang}
            lang={lang}
            onStartSerie={onStartSerie}
          />
        )}

        {/* ── Invite de fusion (cross-domaine) ─────────────────────────────── */}
        {pendingMerge ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-300">{t.mergePrompt}</p>
            <div className="flex gap-2">
              <button onClick={() => resolveMerge(true)} disabled={busy} className={primaryBtn}>{t.mergeYes}</button>
              <button onClick={() => resolveMerge(false)} disabled={busy} className="w-full bg-slate-700 hover:bg-slate-600 text-slate-200 rounded py-2 text-sm transition-colors disabled:opacity-50">{t.mergeNo}</button>
            </div>
          </div>

        /* ── Connecté ──────────────────────────────────────────────────────── */
        ) : auth.account ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-300">{t.loggedInAs(auth.account.handle)}</p>
            <button onClick={auth.logout} className={primaryBtn}>{t.logoutBtn}</button>

            {confirmDelete ? (
              <div className="flex items-center gap-2 mt-1">
                <button onClick={auth.deleteAccount} className="flex-1 bg-red-600 hover:bg-red-500 text-white rounded py-1.5 text-xs transition-colors">{t.deleteConfirm}</button>
                <button onClick={() => setConfirmDelete(false)} className="flex-1 text-slate-400 hover:text-slate-200 text-xs py-1.5">{lang.ui.cancel}</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="text-xs text-slate-600 hover:text-red-400 self-start transition-colors">{t.deleteAccount}</button>
            )}
          </div>

        /* ── Déconnecté : créer / se connecter ─────────────────────────────── */
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-3">{t.intro}</p>

            <div className="flex gap-4 mb-3 text-sm border-b border-slate-700">
              {['create', 'login'].map((m) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(''); }}
                  className={`pb-2 -mb-px border-b-2 transition-colors ${mode === m ? 'border-blue-400 text-blue-400 font-semibold' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                  {m === 'create' ? t.tabCreate : t.tabLogin}
                </button>
              ))}
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
              <input
                type="text" value={handle} onChange={(e) => setHandle(e.target.value)}
                placeholder={t.handlePlaceholder} aria-label={t.handle}
                autoFocus autoComplete="username" autoCapitalize="none" spellCheck="false"
                className={field}
              />
              <input
                type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)}
                placeholder={t.passphrasePlaceholder} aria-label={t.passphrase}
                autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                className={field}
              />
              {mode === 'create' && <p className="text-[11px] text-slate-500 leading-snug">{t.passphraseHint}</p>}
              {error && <p className="text-xs text-red-400">{error}</p>}
              <button type="submit" disabled={busy} className={primaryBtn}>
                {busy ? '…' : (mode === 'create' ? t.createBtn : t.loginBtn)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountPanel;
